import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faSquareArrowUpRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ReactDOMServer from 'react-dom/server';

interface ResolvedLink {
  id: string;
  type: string;
}

const linkIcon = ReactDOMServer.renderToString(<FontAwesomeIcon className="link" icon={faSquareArrowUpRight as IconProp}></FontAwesomeIcon>);

const EXTERNAL_LINK_PREFIX_REGEX = /^(http:|https:|mailto:)/;
const MARKDOWN_LINK_REGEX = /(?<!\\)\[[^\]]*?\]\([^)]*?\)/g;

const renderMarkdownTable = (text: string) => {
  const lines = text.split('\n');

  const isTableLine = (line: string) => line.startsWith('|') && line.trim().endsWith('|');
  const isDelimiterLine = (line: string) => {
    if (!isTableLine(line)) {
      return false;
    }

    const cells = line.trim().slice(1, -1).split('|');
    return cells.length > 0 && cells.every((cell) => /^\s*-{3,}\s*$/.test(cell));
  };

  const flushTable = (rows: { cells: string[]; className?: string }[], render: (line: string) => void) => {
    if (rows.length === 0) {
      return;
    }

    const columnCount = rows.reduce((max, row) => Math.max(max, row.cells.length), 0);

    render(
      `<div class="markdowngridtable" style="grid-template-columns: repeat(${columnCount}, auto)">` +
        rows.map((row) => {
          const paddedCells = [...row.cells, ...new Array(Math.max(0, columnCount - row.cells.length)).fill('')];
          const cells = paddedCells
            .map((cell, index) => {
              const classNames = [
                'markdowngridcell',
                row.className,
                index === 0 ? 'rowfirst' : index === columnCount - 1 ? 'rowlast' : ''
              ].filter(Boolean);
              return `<div class="${classNames.join(' ')}">${cell.trim()}</div>`;
            })
            .join('');
          return cells;
        }).join('') +
      '</div>'
    );
  };

  let tableRows: { cells: string[]; className?: string }[] = [];
  let pendingDelimitedRow = false;
  const renderedLines: string[] = [];
  for (const line of lines) {
    if (isDelimiterLine(line)) {
      pendingDelimitedRow = true;
      continue;
    }

    if (isTableLine(line)) {
      const cells = line.trim().slice(1, -1).split('|');
      tableRows.push({ cells, className: pendingDelimitedRow ? 'delimited' : undefined });
      pendingDelimitedRow = false;
      continue;
    }

    flushTable(tableRows, (line) => renderedLines.push(line));
    pendingDelimitedRow = false;
    tableRows = [];
    renderedLines.push(line);
  }

  flushTable(tableRows, (line) => renderedLines.push(line));
  return renderedLines.join('\n');
};

export const formatMarkup = (markup: string, resolveLocalLink: (linkTarget: string) => ResolvedLink | undefined): string => {
  let formatted = markup;
  // The insane regexes below are to ignore escaped formatting but not be fooled by escaped backslashes.
  formatted = formatted.replace(/(^|[^\\])((?:\\\\)*)\*\*\*((?:\\.|(?!\*\*\*)[^\\])+?)\*\*\*/g, '$1$2<strong><em>$3</em></strong>');
  formatted = formatted.replace(/(^|[^\\])((?:\\\\)*)\*\*((?:\\.|(?!\*\*)[^\\])+?)\*\*/g, '$1$2<strong>$3</strong>');
  formatted = formatted.replace(/(^|[^\\])((?:\\\\)*)\*((?:\\.|[^*\\])+?)\*/g, '$1$2<em>$3</em>');
  formatted = formatted.replace(/(^|[^\\])((?:\\\\)*)`((?:\\.|[^`\\])+)`/g, '$1$2<code>$3</code>');
  formatted = formatted.replace(/(^|[^\\])((?:\\\\)*)\^/g, '$1$2&shy;');

  formatted = formatted.replace(/^---$/gm, '<hr>');

  formatted = formatted.replace(/^- (.+)$/gm, '<ul><li>$1</li></ul>');
  formatted = formatted.replace(/^[0-9]+[.] (.+)$/gm, '<ol><li>$1</li></ol>');
  formatted = formatted.replace(/<\/ul>\n<ul>/g, ''); // Merge consecutive <ul> tags
  formatted = formatted.replace(/<\/ol>\n<ol>/g, ''); // Merge consecutive <ol> tags
  
  formatted = renderMarkdownTable(formatted);

  formatted = formatted.replace(/^&gt; (.*)/gm, '<blockquote>$1</blockquote>');
  formatted = formatted.replace(/<\/blockquote>\n<blockquote>/g, '\n'); // Merge consecutive <blockquote> tags

  formatted = formatted.replace(/^#### (.+)$/gm, '<h4>$1</h4>');
  formatted = formatted.replace(/^### (.+)$/gm, '<h3>$1</h3>');
  formatted = formatted.replace(/^## (.+)$/gm, '<h2>$1</h2>');
  formatted = formatted.replace(/^# (.+)$/gm, '<h1>$1</h1>');

  // Final newline normalization pass.
  // 1) Swallow a single newline before headings.
  // 2) Swallow a single newline after block-level tags.
  // 3) Swallow an additional newline after headings.
  // The div is for rendered tables, which are block-level elements.
  formatted = formatted.replace(/\n(<h[1-4]>)/g, '$1');
  formatted = formatted.replace(/(<hr>|<\/ul>|<\/ol>|<\/blockquote>|<\/div>|<\/h[1-4]>)\n/g, '$1');
  formatted = formatted.replace(/(<\/h[1-4]>)\n/g, '$1');

  formatted = formatted.replace(/\n/g, '<br>');
  formatted = formatted.replace(MARKDOWN_LINK_REGEX, (match) => {
    const linkMatch = match.match(/^\[(.*?)\]\((.*?)\)$/);
    if (linkMatch) {
      const text = linkMatch[1];
      const linkTarget = linkMatch[2].trim();

      if (EXTERNAL_LINK_PREFIX_REGEX.test(linkTarget)) {
        return `<a target='_blank' href="${linkTarget}" class="external">${text} ${linkIcon}</a>`;
      }

      const linkedItemId = resolveLocalLink?.(linkTarget);
      if (linkedItemId) {
        return `<a href='#' data-id='${linkedItemId.id}' class='${linkedItemId.type}'>${text}</a>`;
      }
    }
    return match;
  });
  formatted = formatted.replace(/\\(.)/g, '$1'); // Unescape any escaped characters
  return formatted;
}

export const cleanTextForSearch = (text: string) => {
  // Clean away Markdown
  let cleanText = text
    .split('\n')
    .map((line) => {
      const trimmedLine = line.trim();
      if (!(trimmedLine.startsWith('|') && trimmedLine.endsWith('|'))) {
        return line;
      }

      const cells = trimmedLine.slice(1, -1).split('|').map((cell) => cell.trim());
      if (cells.length > 0 && cells.every((cell) => /^-{3,}$/.test(cell))) {
        return '';
      }

      return cells.join(' ');
    })
    .join('\n');
  
  cleanText = cleanText.replace(/(^|[^\\])((?:\\\\)*)\^/g, '$1$2'); // Remove soft hyphen formatting
  cleanText = cleanText.replace(/(?<!\\)\[([^\]]*)\]\(([^)]+)\)/g, '$1'); // Remove links but keep text
  cleanText = cleanText.replace(/(?<!\\)`([^`]+)`/g, '$1'); // Remove inline code formatting
  cleanText = cleanText.replace(/^>\s*(.*)/gm, '$1'); // Remove blockquote formatting
  cleanText = cleanText.replace(/^#+\s*(.*)/gm, '$1'); // Remove headings
  cleanText = cleanText.replace(/(?<!\\)[*]+/g, ''); // Remove bold/italic formatting
  cleanText = cleanText.replace(/\s+/g, ' ').trim();
  cleanText = cleanText.replace(/\\(.)/g, '$1'); // Unescape any escaped characters
  return cleanText;
}